import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { PublicProfile, UserProfile, Review } from '../types';
import { useTranslation } from '../lib/translations';
import { 
  Search, 
  MapPin, 
  Video, 
  Clock, 
  ChevronRight, 
  Filter, 
  Loader2, 
  Sparkles,
  Zap,
  DollarSign,
  Globe,
  Hash,
  Store,
  CircleDollarSign,
  Languages,
  Sun,
  Moon,
  Navigation,
  Star
} from 'lucide-react';
import Avatar from '../components/Avatar';
import Tooltip from '../components/Tooltip';

interface MarketplaceProps {
  profile: UserProfile | null;
}

const INSTRUMENTS = [
  'Guitar', 'Piano', 'Voice', 'Drums', 'Violin', 'Bass', 
  'Double Bass', 'Saxophone', 'Trumpet', 'Flute', 'Cello', 
  'Music Theory', 'Composition', 'Mixing', 'Producer'
];

const DISTANCES = [5, 10, 20, 50, 100];

function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371; // km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

const Marketplace: React.FC<MarketplaceProps> = ({ profile }) => {
  const { t, language, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [teachers, setTeachers] = useState<PublicProfile[]>([]);
  const [reviewsMap, setReviewsMap] = useState<Record<string, { avg: number, count: number }>>({});
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));
  
  const [search, setSearch] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [maxPrice, setMaxPrice] = useState(100);
  const [remoteOnly, setRemoteOnly] = useState(false);

  const [userCoords, setUserCoords] = useState<{ lat: number, lng: number } | null>(null);
  const [maxDistance, setMaxDistance] = useState<number | null>(null);
  const [locLoading, setLocLoading] = useState(false);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'public_profiles'), 
          where('isPublished', '==', true),
          orderBy('updatedAt', 'desc')
        );
        const snap = await getDocs(q);
        const teachersData = snap.docs.map(d => d.data() as PublicProfile);
        setTeachers(teachersData);

        const revSnap = await getDocs(collection(db, 'reviews'));
        const map: Record<string, { total: number, count: number }> = {};
        revSnap.docs.forEach(doc => {
          const r = doc.data() as Review;
          if (!map[r.teacherId]) map[r.teacherId] = { total: 0, count: 0 };
          map[r.teacherId].total += r.rating;
          map[r.teacherId].count += 1;
        });
        
        const finalMap: Record<string, { avg: number, count: number }> = {};
        Object.keys(map).forEach(tid => {
          finalMap[tid] = {
            avg: parseFloat((map[tid].total / map[tid].count).toFixed(1)),
            count: map[tid].count
          };
        });
        setReviewsMap(finalMap);

      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleRequestLocation = () => {
    if (!navigator.geolocation) return;
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocLoading(false);
        if (!maxDistance) setMaxDistance(20);
      },
      (err) => {
        console.error(err);
        setLocLoading(false);
        alert(t.marketplace.locationError);
      }
    );
  };

  const filteredTeachers = useMemo(() => {
    let result = teachers.filter(t => {
      const matchSearch = t.displayName.toLowerCase().includes(search.toLowerCase()) || 
                          t.tagline.toLowerCase().includes(search.toLowerCase());
      const matchInst = !selectedInstrument || t.instruments.includes(selectedInstrument);
      const matchPrice = t.pricing.hourlyRate <= maxPrice;
      const matchRemote = !remoteOnly || t.location.remote;
      
      let matchDistance = true;
      if (userCoords && maxDistance && t.location.coords) {
        const dist = calculateDistance(userCoords.lat, userCoords.lng, t.location.coords.lat, t.location.coords.lng);
        matchDistance = dist <= maxDistance;
      }

      return matchSearch && matchInst && matchPrice && matchRemote && matchDistance;
    });

    if (userCoords) {
      result = [...result].sort((a, b) => {
        if (!a.location.coords) return 1;
        if (!b.location.coords) return -1;
        const distA = calculateDistance(userCoords.lat, userCoords.lng, a.location.coords.lat, a.location.coords.lng);
        const distB = calculateDistance(userCoords.lat, userCoords.lng, b.location.coords.lat, b.location.coords.lng);
        return distA - distB;
      });
    }

    return result;
  }, [teachers, search, selectedInstrument, maxPrice, remoteOnly, userCoords, maxDistance]);

  const content = (
    <div className="space-y-12 pb-32">
      <div className="text-center space-y-4 pt-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-[0.2em] shadow-sm mb-2">
          <Sparkles size={12} className="animate-pulse" /> {t.nav.marketplace}
        </div>
        <h1 className="text-5xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">
          {t.marketplace.title}
        </h1>
        <p className="text-slate-500 font-medium text-lg max-w-2xl mx-auto">
          {t.marketplace.subtitle}
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3 space-y-8">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-2xl p-6 shadow-xl space-y-8">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              <Filter size={14} className="text-purple-600" /> {t.marketplace.filters}
            </div>
            <div className="space-y-2">
               <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t.marketplace.instrument}</label>
               <select value={selectedInstrument} onChange={(e) => setSelectedInstrument(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-2.5 px-3 text-xs font-bold focus:ring-4 focus:ring-purple-500/10 outline-none appearance-none cursor-pointer"><option value="">{t.marketplace.allCategories}</option>{INSTRUMENTS.map(i => <option key={i} value={i}>{i}</option>)}</select>
            </div>
            <div className="space-y-2">
               <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t.marketplace.nearMe}</label>
               {!userCoords ? (
                 <button onClick={handleRequestLocation} disabled={locLoading} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-white/5 rounded-lg py-2.5 px-3 text-xs font-bold flex items-center justify-center gap-2 text-purple-600 hover:bg-slate-100 dark:hover:bg-white/10 transition-all">{locLoading ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}{t.marketplace.nearMe}</button>
               ) : (
                 <div className="space-y-3 animate-in slide-in-from-top-1"><select value={maxDistance || ''} onChange={(e) => setMaxDistance(e.target.value ? parseInt(e.target.value) : null)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-2.5 px-3 text-xs font-bold focus:ring-4 focus:ring-purple-500/10 outline-none"><option value="">{t.marketplace.allDistances}</option>{DISTANCES.map(d => <option key={d} value={d}>{t.marketplace.distance.replace('{distance}', d.toString())}</option>)}</select><button onClick={() => setUserCoords(null)} className="text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-red-500 transition-colors w-full text-center">{t.marketplace.resetLocation}</button></div>
               )}
            </div>
            <div className="space-y-4">
               <div className="flex justify-between items-center"><label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t.marketplace.priceRange}</label><span className="text-xs font-black text-purple-600">€{maxPrice}</span></div>
               <input type="range" min="10" max="200" step="5" value={maxPrice} onChange={(e) => setMaxPrice(parseInt(e.target.value))} className="w-full accent-purple-600" />
            </div>
            <button onClick={() => setRemoteOnly(!remoteOnly)} className={`flex items-center gap-3 p-3 w-full rounded-xl border transition-all ${remoteOnly ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-500/20 text-teal-700 dark:text-teal-400 shadow-inner' : 'bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-white/5 text-slate-500'}`}><div className={`p-1.5 rounded-lg ${remoteOnly ? 'bg-teal-500 text-white' : 'bg-slate-200 dark:bg-slate-800'}`}><Video size={14} /></div><span className="text-xs font-bold leading-tight">{t.marketplace.remoteOnly}</span></button>
          </div>
          <div className="bg-slate-900 rounded-2xl p-8 text-white relative overflow-hidden group shadow-2xl"><div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-125 transition-transform duration-700"><Zap size={80} /></div><h4 className="text-xl font-black mb-2 leading-tight">{t.marketplace.teachingProfessionallyTitle}</h4><p className="text-slate-400 text-sm font-medium mb-6">{t.marketplace.teachingProfessionallyDesc}</p><button onClick={() => navigate('/public-profile')} className="w-full py-3 bg-purple-600 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-purple-700 transition-all shadow-lg shadow-purple-500/20">{t.pricing.getStarted}</button></div>
        </aside>

        <div className="lg:col-span-9 space-y-6">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" size={20} />
            <input 
              type="text" 
              placeholder={t.marketplace.searchPlaceholder} 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-2xl py-4 px-12 text-sm font-medium focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none shadow-sm transition-all" 
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-purple-600" size={40} />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filteredTeachers.length === 0 ? (
                <div className="md:col-span-2 py-20 text-center border-2 border-dashed border-slate-200 dark:border-white/5 rounded-2xl">
                  <Search size={48} className="text-slate-200 dark:text-slate-800 mx-auto mb-4" />
                  <p className="text-slate-400 font-bold italic">{t.marketplace.noTeachers}</p>
                </div>
              ) : (
                filteredTeachers.map(teacher => {
                  const revs = reviewsMap[teacher.uid] || { avg: 0, count: 0 };
                  return (
                    <Link 
                      key={teacher.uid} 
                      to={`/teacher/${teacher.uid}`}
                      className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-2xl p-6 hover:border-purple-500/30 hover:shadow-2xl transition-all flex flex-col"
                    >
                      <div className="flex items-start gap-4 mb-6">
                        <Avatar src={teacher.avatarUrl} name={teacher.displayName} className="w-16 h-16 rounded-xl" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-black text-slate-900 dark:text-white truncate group-hover:text-purple-600 transition-colors">{teacher.displayName}</h3>
                          <p className="text-xs font-bold text-purple-600 dark:text-purple-400 truncate mb-2">{teacher.tagline}</p>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                              <MapPin size={12} className="text-purple-500" /> {teacher.location.city}
                            </div>
                            {revs.count > 0 && (
                              <div className="flex items-center gap-1 text-[10px] font-black uppercase text-amber-500 tracking-widest">
                                <Star size={12} fill="currentColor" /> {revs.avg} ({revs.count})
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-6 flex-1">
                        {teacher.instruments.slice(0, 3).map((inst, i) => (
                          <span key={i} className="px-3 py-1 rounded-full bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest border border-slate-100 dark:border-white/5">
                            {inst}
                          </span>
                        ))}
                        {teacher.instruments.length > 3 && (
                          <span key="more" className="px-3 py-1 rounded-full bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest border border-slate-100 dark:border-white/5">
                            +{teacher.instruments.length - 3}
                          </span>
                        )}
                      </div>

                      <div className="pt-6 border-t border-slate-100 dark:border-white/5 flex items-center justify-between">
                        <div>
                          <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1">{t.marketplace.startingFrom}</p>
                          <p className="text-xl font-black text-slate-900 dark:text-white">€{teacher.pricing.hourlyRate}<span className="text-xs font-bold text-slate-400 ml-1">/h</span></p>
                        </div>
                        <div className="p-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 rounded-lg group-hover:bg-purple-600 group-hover:text-white transition-all">
                          <ChevronRight size={20} />
                        </div>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#020617] transition-colors duration-300">
        <nav className="fixed top-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] md:w-[calc(100%-3rem)] max-w-6xl z-50 glass-card rounded-2xl px-6 h-16 flex items-center justify-between shadow-2xl border-white/40 dark:border-white/5">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="p-2 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg shadow-lg group-hover:scale-105 transition-transform">
              <Hash size={18} className="text-white" />
            </div>
            <span className="hidden sm:block text-lg font-black tracking-tight text-slate-900 dark:text-white">SharpNote</span>
          </Link>
          <div className="flex items-center gap-4 md:gap-8">
            <Link to="/teachers" className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/teachers' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}><Store size={16} />{t.nav.marketplace}</Link>
            <Link to="/pricing" className={`hidden md:flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/pricing' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}><CircleDollarSign size={16} />{t.nav.pricing}</Link>
            <div className="flex items-center gap-3">
              <button onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')} className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest"><Languages size={16} />{language.toUpperCase()}</button>
              <button onClick={() => setIsDark(!isDark)} className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all">{isDark ? <Sun size={18} /> : <Moon size={18} />}</button>
              <Link to="/login" className="px-6 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-950 font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-xl">{t.landing.enterApp}</Link>
            </div>
          </div>
        </nav>
        <div className="fixed inset-0 overflow-hidden pointer-events-none"><div className="absolute -top-[15%] left-[20%] w-[60%] h-[60%] bg-purple-600/10 dark:bg-purple-600/20 blur-[120px] rounded-full opacity-40"></div></div>
        <main className="pt-32 pb-20 px-6 max-w-7xl mx-auto">{content}</main>
      </div>
    );
  }

  return (
    <div className="pt-10">
      {content}
    </div>
  );
};

export default Marketplace;
